/**
 * Pure utility functions for the Assignments tab.
 *
 * All functions in this module are pure: they take a
 * `StationAssignmentHalfData` (or related input) and return a new value
 * without mutating the input. This makes the assignment logic trivially
 * unit-testable and amenable to property-based testing.
 *
 * The authoritative location for an associate is the
 * `processPaths[*].workstations[*].assignedAssociates`
 * structure. The `assignedWorkstationId` field on entries in
 * `response.associates` is a convenience marker kept in sync by these
 * helpers.
 */

import {
  AssignmentAssociate,
  AssignmentDecision,
  AssignmentsMetrics,
  StationAssignmentHalfData,
  ProcessPath,
  PathType,
  Workstation,
  isEmptySlot,
} from './models';

const PRIMARY_PATH_NAME = 'Primary';
const CLEAN_DECANT_PATH_NAME = 'Clean decant';
const RESALE_PATH_NAME = 'Resale';
const PROBLEM_SOLVE_PATH_NAME = 'Problem solve';

/**
 * Return `true` if the given associate currently appears in any workstation
 * or non-workstation-associate list across the provided `processPaths`.
 *
 * Used by the AssociateSidebar to compute the unassigned pool:
 *   `response.associates.filter(a => !isAssigned(a, response.processPaths))`.
 */
export function isAssigned(
  associate: AssignmentAssociate,
  processPaths: ProcessPath[]
): boolean {
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      if (ws.assignedAssociates.some(a => a.associateId === associate.associateId)) {
        return true;
      }
    }
    if (path.nonWorkstationAssociates?.some(a => a.associateId === associate.associateId)) {
      return true;
    }
  }
  return false;
}

/**
 * Derive the PA/Lead/HR pool for a shift half: the associates in
 * `unassignedPALeadHRAssociates` who are not currently placed in any workstation
 * or non-workstation list. Mirrors how the regular unassigned pool is derived
 * (`associates.filter(a => !isAssigned(a, processPaths))`) so the "PA / Lead /
 * HR" box and its count badge stay consistent with the board.
 *
 * Accepts any object carrying `unassignedPALeadHRAssociates` + `processPaths`
 * (a `StationAssignmentHalfData`, or the published screen's flat view-model
 * which has no `metrics`), since those are the only fields it reads.
 *
 * Null-safe: a missing `unassignedPALeadHRAssociates` yields an empty pool.
 */
export function getPALeadHRPool(
  half: Pick<StationAssignmentHalfData, 'processPaths'> & {
    unassignedPALeadHRAssociates?: AssignmentAssociate[];
  }
): AssignmentAssociate[] {
  return (half.unassignedPALeadHRAssociates ?? []).filter(
    a => !isAssigned(a, half.processPaths)
  );
}

/**
 * Build an empty (planned-but-unfilled) placeholder slot — the shape the API
 * returns for an unfilled planned position, and what {@link isEmptySlot}
 * matches (no `associateId`). `assignedWorkstationId` is carried for a
 * workstation slot so the freed station stays on the line; it is omitted for a
 * non-workstation (pool) entry, which has no per-station id.
 *
 * The {@link SlotProperties} are durable properties of the physical slot (e.g.
 * a `ws-04-12-R` station is a RIGHT station whether or not it is occupied), so
 * they are carried forward when a workstation slot is emptied — they must
 * survive on publish for the now-vacant slot. They are omitted for
 * non-workstation entries, which are not station slots.
 */
function makeEmptySlot(
  assignedWorkstationId?: string,
  slotProperties: SlotProperties = NO_SLOT_PROPERTIES,
): AssignmentAssociate {
  return {
    associateName: '',
    isClocked: false,
    hasWarning: false,
    trainedPaths: [],
    assignedWorkstationId,
    ...slotProperties,
  };
}

/**
 * Fields on an {@link AssignmentAssociate} entry that describe the physical
 * workstation slot rather than the person in it: ergonomic orientation,
 * front/back zone and position within the line. They arrive from the facade
 * on assigned and empty slots and must publish back on the same slot. When an
 * associate moves, these stay with the slot; the associate takes the
 * destination slot's values, and an associate in a pool or an overflow slot
 * has none.
 */
type SlotProperties = Pick<
  AssignmentAssociate,
  'ergonomicProperty' | 'stationPosition' | 'workstationSortOrder'
>;

/** Clears every {@link SlotProperties} field when spread onto an entry. */
const NO_SLOT_PROPERTIES: SlotProperties = {
  ergonomicProperty: undefined,
  stationPosition: undefined,
  workstationSortOrder: undefined,
};

/** Read the {@link SlotProperties} of a workstation slot entry. */
function slotPropertiesOf(slot: AssignmentAssociate): SlotProperties {
  return {
    ergonomicProperty: slot.ergonomicProperty,
    stationPosition: slot.stationPosition,
    workstationSortOrder: slot.workstationSortOrder,
  };
}

/**
 * Remove an associate from every workstation and every non-workstation-associate list.
 *
 * Both a workstation slot and a `nonWorkstationAssociates` entry are treated as
 * fixed planned positions: the associate is replaced with an empty placeholder
 * rather than deleted, so the planned count stays constant and the freed
 * position can be re-filled — symmetric with {@link fillEmptyStationSlot} /
 * {@link fillEmptyNonWorkstationSlot}, which fill placeholders in place.
 *
 * Returns a new `processPaths` array and a flag indicating whether any
 * change was made. If nothing changed, the original `processPaths`
 * reference is returned so callers can skip further copying.
 */
function removeAssociateFromAllLocations(
  processPaths: ProcessPath[],
  associateId: string
): { processPaths: ProcessPath[]; changed: boolean } {
  let changed = false;

  const newPaths = processPaths.map(path => {
    let pathChanged = false;

    const newWorkstations = path.workstations.map(ws => {
      const idx = ws.assignedAssociates.findIndex(a => a.associateId === associateId);
      if (idx === -1) {
        return ws;
      }
      pathChanged = true;
      // A workstation slot is a fixed planned position: replace the associate
      // with an empty placeholder that keeps the slot's per-station id and
      // slot properties (orientation, zone, position), so the freed station stays on the line and can
      // be assigned again — rather than deleting the slot (which would shrink
      // the line's planned count).
      const slots = ws.assignedAssociates.map((slot, i) =>
        i === idx ? makeEmptySlot(slot.assignedWorkstationId, slotPropertiesOf(slot)) : slot
      );
      return { ...ws, assignedAssociates: slots };
    });

    // A non-workstation entry is likewise a fixed planned position: replace it
    // with an empty placeholder (no per-station id) instead of filtering it
    // out, so the pool's planned count stays constant and the freed slot can be
    // re-filled — matching how workstation slots and the fill helpers behave.
    let newNonWorkstation = path.nonWorkstationAssociates;
    if (path.nonWorkstationAssociates) {
      const idx = path.nonWorkstationAssociates.findIndex(a => a.associateId === associateId);
      if (idx !== -1) {
        pathChanged = true;
        newNonWorkstation = path.nonWorkstationAssociates.map((entry, i) =>
          i === idx ? makeEmptySlot() : entry
        );
      }
    }

    if (pathChanged) {
      changed = true;
      return {
        ...path,
        workstations: newWorkstations,
        nonWorkstationAssociates: newNonWorkstation,
      };
    }
    return path;
  });

  return { processPaths: changed ? newPaths : processPaths, changed };
}

/**
 * Find a workstation by id across the provided `processPaths`.
 */
function findWorkstationById(
  processPaths: ProcessPath[],
  workstationId: string
): Workstation | null {
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      if (ws.workstationId === workstationId) {
        return ws;
      }
    }
  }
  return null;
}

/**
 * Resolve an associate for an assign/fill operation by searching, in order:
 * the regular unassigned pool (`response.associates`), the PA/Lead/HR pool
 * (`response.unassignedPALeadHRAssociates`), then every workstation and
 * non-workstation list (so a move from an already-placed location works).
 *
 * Returns the resolved associate together with `fromPALeadHRPool`, which is
 * `true` only when the associate was found in the PA/Lead/HR pool. Callers use
 * that flag to stamp `isPALeadHR` on the placed associate (Model A identity) so
 * a later unassign returns them to the PA/Lead/HR box. Returns `null` when the
 * associate cannot be found anywhere.
 */
function resolveAssociateForAssign(
  response: StationAssignmentHalfData,
  associateId: string
): { associate: AssignmentAssociate; fromPALeadHRPool: boolean } | null {
  const pooled = response.associates.find(a => a.associateId === associateId);
  if (pooled) {
    return { associate: pooled, fromPALeadHRPool: false };
  }
  const paLeadHR = (response.unassignedPALeadHRAssociates ?? []).find(
    a => a.associateId === associateId
  );
  if (paLeadHR) {
    return { associate: paLeadHR, fromPALeadHRPool: true };
  }
  for (const path of response.processPaths) {
    for (const ws of path.workstations) {
      const found = ws.assignedAssociates.find(a => a.associateId === associateId);
      if (found) {
        return { associate: found, fromPALeadHRPool: false };
      }
    }
    const foundNonWorkstation = path.nonWorkstationAssociates?.find(
      a => a.associateId === associateId
    );
    if (foundNonWorkstation) {
      return { associate: foundNonWorkstation, fromPALeadHRPool: false };
    }
  }
  return null;
}

/**
 * Return a copy of `unassignedPALeadHRAssociates` with the given associate
 * removed. Unlike a workstation slot, the PA/Lead/HR pool has no fixed planned
 * positions, so the entry is filtered out (not replaced with a placeholder) —
 * matching how the regular unassigned pool is derived by membership. Null-safe:
 * an absent pool yields `undefined` unchanged.
 */
function removeFromPALeadHRPool(
  pool: AssignmentAssociate[] | undefined,
  associateId: string
): AssignmentAssociate[] | undefined {
  /* istanbul ignore if -- defensive: callers only reach removal when the associate
     was resolved FROM this pool, so the pool is always defined here */
  if (!pool) {
    return pool;
  }
  return pool.filter(a => a.associateId !== associateId);
}

/**
 * Assign an associate to the given workstation.
 *
 * Behavior:
 * - Pure: returns a new `StationAssignmentHalfData`; the input is not mutated.
 * - If `workstationId` does not exist, returns the input unchanged.
 * - If `associateId` does not exist in `response.associates` or any
 *   workstation, returns the input unchanged.
 * - If the associate is currently assigned elsewhere (a workstation or a
 *   non-workstation-associate list), they are removed from that location first.
 * - `response.associates[i].assignedWorkstationId` is kept in sync with the
 *   authoritative path/workstation structure.
 */
export function assignAssociateToWorkstation(
  response: StationAssignmentHalfData,
  associateId: string,
  workstationId: string
): StationAssignmentHalfData {
  if (!findWorkstationById(response.processPaths, workstationId)) {
    return response;
  }

  // Try to find the associate in the unassigned pool first.
  let canonicalIdx = response.associates.findIndex(a => a.associateId === associateId);
  let associate: AssignmentAssociate | undefined;

  if (canonicalIdx !== -1) {
    associate = response.associates[canonicalIdx];
  } else {
    // Associate is not in the unassigned pool — look in workstations, then in
    // the non-workstation (department/indirect) associate lists. The real API
    // places assigned associates only inside `workstations`/
    // `nonWorkstationAssociates`, never in the top-level pool, so both must be
    // searched for the move to succeed.
    for (const path of response.processPaths) {
      for (const ws of path.workstations) {
        const found = ws.assignedAssociates.find(a => a.associateId === associateId);
        if (found) {
          associate = found;
          break;
        }
      }
      if (associate) break;
      const foundNonWorkstation = path.nonWorkstationAssociates?.find(
        a => a.associateId === associateId
      );
      if (foundNonWorkstation) {
        associate = foundNonWorkstation;
        break;
      }
    }
  }

  if (!associate) {
    return response;
  }

  // Remove from any prior location (workstation or non-workstation-associate list).
  const { processPaths: pathsWithoutAssociate } = removeAssociateFromAllLocations(
    response.processPaths,
    associateId
  );

  // Build the updated associate entry with the new workstation id. Clear any
  // slot properties carried on the associate: they belong to the destination
  // slot, not the person, so they are applied per-slot at insertion time below
  // (and must never follow an associate from a prior slot).
  const updatedAssociate: AssignmentAssociate = {
    ...associate,
    assignedWorkstationId: workstationId,
    ...NO_SLOT_PROPERTIES,
  };

  // Update the associates array: if found in pool, update in place; otherwise keep as is.
  const newAssociates = canonicalIdx !== -1
    ? response.associates.map((a, i) => i === canonicalIdx ? updatedAssociate : a)
    : [...response.associates];

  // Insert the associate into the target workstation. Prefer filling an
  // existing empty (planned-but-unfilled) placeholder so the line's planned
  // slot count stays balanced — this matters when the associate is *moved*
  // from another workstation, where removal above leaves an empty placeholder
  // on the source (net 0). Only when the target has no empty placeholder do we
  // append (overflow), mirroring {@link fillEmptyStationSlot}. The filled entry
  // inherits the destination slot's properties (fixed properties of the
  // physical station); an overflow slot has none.
  const newProcessPaths = pathsWithoutAssociate.map(path => ({
    ...path,
    workstations: path.workstations.map(ws => {
      if (ws.workstationId !== workstationId) {
        return ws;
      }
      const emptyIdx = ws.assignedAssociates.findIndex(isEmptySlot);
      if (emptyIdx === -1) {
        return { ...ws, assignedAssociates: [...ws.assignedAssociates, updatedAssociate] };
      }
      const slots = ws.assignedAssociates.map((slot, i) =>
        i === emptyIdx
          ? { ...updatedAssociate, ...slotPropertiesOf(slot) }
          : slot
      );
      return { ...ws, assignedAssociates: slots };
    }),
  }));

  return {
    ...response,
    associates: newAssociates,
    processPaths: newProcessPaths,
  };
}

/**
 * Fill a specific empty (planned-but-unfilled) slot on a line with an
 * associate — the "add associate on a line" interaction of the redesigned
 * screen.
 *
 * Unlike {@link assignAssociateToWorkstation}, which appends the associate to
 * the workstation's list, this **replaces** the placeholder entry at
 * `slotIndex`, so the line's `filled/total` (and its capacity of planned
 * positions) stays correct. The placeholder's `assignedWorkstationId` (the
 * per-station id, e.g. "ws-05-03-R") is preserved on the now-filled slot.
 *
 * Behavior:
 * - Pure: returns a new `StationAssignmentHalfData`; the input is not mutated.
 * - Returns the input unchanged when the workstation or associate can't be
 *   resolved, or when `slotIndex` is out of range.
 * - Removes the associate from any prior location first, so filling can't
 *   duplicate an already-assigned associate. If that removal shifts indices
 *   within the same target workstation, the fill falls back to the first
 *   remaining empty slot (then to an overflow append) so the associate always
 *   lands on the intended line.
 * - Keeps `response.associates[i].assignedWorkstationId` in sync.
 */
export function fillEmptyStationSlot(
  response: StationAssignmentHalfData,
  associateId: string,
  workstationId: string,
  slotIndex: number
): StationAssignmentHalfData {
  const targetWorkstation = findWorkstationById(response.processPaths, workstationId);
  if (!targetWorkstation) {
    return response;
  }
  if (slotIndex < 0 || slotIndex >= targetWorkstation.assignedAssociates.length) {
    return response;
  }

  // Resolve the associate: unassigned pool, PA/Lead/HR pool, then any
  // workstation or non-workstation list (so a move from another location works).
  const resolved = resolveAssociateForAssign(response, associateId);
  if (!resolved) {
    return response;
  }
  const { associate, fromPALeadHRPool } = resolved;
  const canonicalIdx = response.associates.findIndex(a => a.associateId === associateId);

  // The placeholder we intend to fill — captured before any removal shifts it.
  const placeholder = targetWorkstation.assignedAssociates[slotIndex];

  // Remove from any prior location so a move can't duplicate the associate.
  const { processPaths: cleanedPaths } = removeAssociateFromAllLocations(
    response.processPaths,
    associateId
  );

  const filled: AssignmentAssociate = {
    ...associate,
    assignedWorkstationId: placeholder.assignedWorkstationId ?? workstationId,
    // Clear any slot properties carried on the associate: they belong to the
    // destination slot, not the person, and are applied per-slot at insertion
    // time below.
    ...NO_SLOT_PROPERTIES,
    // Stamp the durable PA/Lead/HR marker when the associate is assigned OUT of
    // the PA/Lead/HR pool, so a later unassign returns them there (Model A).
    ...(fromPALeadHRPool ? { isPALeadHR: true } : {}),
  };

  const newProcessPaths = cleanedPaths.map(path => ({
    ...path,
    workstations: path.workstations.map(ws => {
      if (ws.workstationId !== workstationId) {
        return ws;
      }
      const slots = [...ws.assignedAssociates];
      // Prefer the requested slot; if removal above shifted it (associate was
      // previously on this same line), fall back to the first empty slot, then
      // to an overflow append. The filled entry inherits the destination slot's
      // properties (fixed properties of the physical station); an overflow
      // slot has none.
      const idx = isEmptySlot(slots[slotIndex])
        ? slotIndex
        : slots.findIndex(isEmptySlot);
      if (idx === -1) {
        slots.push(filled);
      } else {
        slots[idx] = { ...filled, ...slotPropertiesOf(slots[idx]) };
      }
      return { ...ws, assignedAssociates: slots };
    }),
  }));

  const newAssociates = canonicalIdx !== -1
    ? response.associates.map((a, i) => (i === canonicalIdx ? filled : a))
    : [...response.associates];

  // If the associate came from the PA/Lead/HR pool, drop them from it — they
  // now live in a process path (carrying isPALeadHR).
  const newPALeadHRPool = fromPALeadHRPool
    ? removeFromPALeadHRPool(response.unassignedPALeadHRAssociates, associateId)
    : response.unassignedPALeadHRAssociates;

  return {
    ...response,
    associates: newAssociates,
    unassignedPALeadHRAssociates: newPALeadHRPool,
    processPaths: newProcessPaths,
  };
}

/**
 * Fill a specific empty (planned-but-unfilled) slot in a function's
 * `nonWorkstationAssociates` list — the "add associate" interaction for
 * no-lane, non-workstation paths (e.g. INDIRECT functions whose planned
 * positions arrive as placeholder entries in `nonWorkstationAssociates` with
 * no `associateId`/`associateName`/`associateAlias`).
 *
 * Mirrors {@link fillEmptyStationSlot} but targets a process path by
 * `fclmFunctionId` and its `nonWorkstationAssociates` array instead of a
 * workstation. There is no per-station id, so the filled entry's
 * `assignedWorkstationId` is cleared.
 *
 * Behavior:
 * - Pure: returns a new `StationAssignmentHalfData`; the input is not mutated.
 * - Returns the input unchanged when the path (or its
 *   `nonWorkstationAssociates`) or the associate can't be resolved, or when
 *   `slotIndex` is out of range.
 * - Removes the associate from any prior location first; if that shifts the
 *   index, falls back to the first remaining empty slot (then overflow-append).
 * - Keeps `response.associates[i].assignedWorkstationId` in sync.
 */
export function fillEmptyNonWorkstationSlot(
  response: StationAssignmentHalfData,
  associateId: string,
  fclmFunctionId: string,
  slotIndex: number
): StationAssignmentHalfData {
  const targetPath = response.processPaths.find(p => p.fclmFunctionId === fclmFunctionId);
  if (!targetPath || !targetPath.nonWorkstationAssociates) {
    return response;
  }
  if (slotIndex < 0 || slotIndex >= targetPath.nonWorkstationAssociates.length) {
    return response;
  }

  // Resolve the associate: unassigned pool, PA/Lead/HR pool, then any
  // workstation or non-workstation list.
  const resolved = resolveAssociateForAssign(response, associateId);
  if (!resolved) {
    return response;
  }
  const { associate, fromPALeadHRPool } = resolved;
  const canonicalIdx = response.associates.findIndex(a => a.associateId === associateId);

  // Remove from any prior location so a move can't duplicate the associate.
  const { processPaths: cleanedPaths } = removeAssociateFromAllLocations(
    response.processPaths,
    associateId
  );

  // A non-workstation associate is assigned to the function, not a station, so
  // it has no per-station id and no slot properties (those describe a physical
  // station slot).
  const filled: AssignmentAssociate = {
    ...associate,
    assignedWorkstationId: undefined,
    ...NO_SLOT_PROPERTIES,
    // Stamp the durable PA/Lead/HR marker when assigned OUT of that pool (Model A).
    ...(fromPALeadHRPool ? { isPALeadHR: true } : {}),
  };

  const newProcessPaths = cleanedPaths.map(path => {
    if (path.fclmFunctionId !== fclmFunctionId || !path.nonWorkstationAssociates) {
      return path;
    }
    const slots = [...path.nonWorkstationAssociates];
    const idx = isEmptySlot(slots[slotIndex])
      ? slotIndex
      : slots.findIndex(isEmptySlot);
    if (idx === -1) {
      slots.push(filled);
    } else {
      slots[idx] = filled;
    }
    return { ...path, nonWorkstationAssociates: slots };
  });

  const newAssociates = canonicalIdx !== -1
    ? response.associates.map((a, i) => (i === canonicalIdx ? filled : a))
    : [...response.associates];

  const newPALeadHRPool = fromPALeadHRPool
    ? removeFromPALeadHRPool(response.unassignedPALeadHRAssociates, associateId)
    : response.unassignedPALeadHRAssociates;

  return {
    ...response,
    associates: newAssociates,
    unassignedPALeadHRAssociates: newPALeadHRPool,
    processPaths: newProcessPaths,
  };
}

/**
 * Remove an associate from a specific workstation.
 *
 * `locationId` is the `workstationId` where the associate currently sits.
 *
 * Behavior:
 * - Pure: returns a new `StationAssignmentHalfData`; the input is not mutated.
 * - If the associate is not currently at the given location, returns the
 *   input unchanged.
 * - Clears `assignedWorkstationId` on the moved associate in
 *   `response.associates`.
 */
export function removeAssociateFromWorkstation(
  response: StationAssignmentHalfData,
  associateId: string,
  locationId: string
): StationAssignmentHalfData {
  let foundInWorkstation = false;

  for (const path of response.processPaths) {
    for (const ws of path.workstations) {
      if (
        ws.workstationId === locationId &&
        ws.assignedAssociates.some(a => a.associateId === associateId)
      ) {
        foundInWorkstation = true;
        break;
      }
    }
    if (foundInWorkstation) break;
  }

  if (!foundInWorkstation) {
    return response;
  }

  const newProcessPaths = response.processPaths.map(path => {
    const newWorkstations = path.workstations.map(ws =>
      ws.workstationId === locationId
        ? {
            ...ws,
            assignedAssociates: ws.assignedAssociates.filter(
              a => a.associateId !== associateId
            ),
          }
        : ws
    );

    return {
      ...path,
      workstations: newWorkstations,
    };
  });

  const newAssociates = response.associates.map(a =>
    a.associateId === associateId
      ? { ...a, assignedWorkstationId: undefined, ...NO_SLOT_PROPERTIES }
      : a
  );

  return {
    ...response,
    associates: newAssociates,
    processPaths: newProcessPaths,
  };
}

/**
 * Filter an array of associates by a case-insensitive substring match on
 * either the `associateName` or the login `associateAlias` — so planners can
 * find someone by full name or by alias (the alias is now the primary display
 * label).
 *
 * If `searchQuery` is empty or whitespace-only, the input array is returned
 * unchanged (the same reference).
 */
export function filterUnassignedAssociates(
  associates: AssignmentAssociate[],
  searchQuery: string
): AssignmentAssociate[] {
  if (!searchQuery || searchQuery.trim().length === 0) {
    return associates;
  }
  const needle = searchQuery.toLowerCase();
  return associates.filter(
    a =>
      a.associateName.toLowerCase().includes(needle) ||
      (a.associateAlias?.toLowerCase().includes(needle) ?? false)
  );
}

/**
 * Return the associate entry currently assigned under `associateId`, searching
 * workstation slots first, then non-workstation (department) lists. Returns
 * `null` when the associate is not assigned anywhere.
 *
 * Shared by the page's open handlers (move / unassign / view-decision) so the
 * lookup stays consistent if the assignment data structure ever changes, and
 * reused internally by {@link unassignAssociate}.
 */
export function findAssignedAssociate(
  processPaths: ProcessPath[],
  associateId: string
): AssignmentAssociate | null {
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      const found = ws.assignedAssociates.find(a => a.associateId === associateId);
      if (found) {
        return found;
      }
    }
    const foundNonWorkstation = path.nonWorkstationAssociates?.find(
      a => a.associateId === associateId
    );
    if (foundNonWorkstation) {
      return foundNonWorkstation;
    }
  }
  return null;
}

/**
 * Unassign an associate: remove them from wherever they currently sit and
 * return them to the top-level unassigned pool.
 *
 * Behavior:
 * - Pure: returns a new `StationAssignmentHalfData`; the input is not mutated.
 * - Returns the input unchanged when the associate is not assigned anywhere.
 * - When the associate sits in a **workstation slot**, that slot is replaced
 *   by an empty (planned-but-unfilled) placeholder that keeps the slot's
 *   per-station id (`assignedWorkstationId`), so the line's planned position
 *   count is preserved and the freed station can be filled again.
 * - When the associate sits in a **non-workstation (department) list**, they
 *   are removed from that list (department lists have no fixed planned slots).
 * - The associate is re-added to `response.associates` (with
 *   `assignedWorkstationId` cleared) if not already present, so the sidebar
 *   pool shows them again.
 */
export function unassignAssociate(
  response: StationAssignmentHalfData,
  associateId: string
): StationAssignmentHalfData {
  const assigned = findAssignedAssociate(response.processPaths, associateId);
  if (!assigned) {
    return response;
  }

  const newProcessPaths = response.processPaths.map(path => {
    let pathChanged = false;

    // Replace the associate's workstation slot with an empty placeholder that
    // retains the station id, so the planned position remains on the line.
    const newWorkstations = path.workstations.map(ws => {
      const idx = ws.assignedAssociates.findIndex(a => a.associateId === associateId);
      if (idx === -1) {
        return ws;
      }
      pathChanged = true;
      const slots = ws.assignedAssociates.map((slot, i) =>
        i === idx ? makeEmptySlot(slot.assignedWorkstationId, slotPropertiesOf(slot)) : slot
      );
      return { ...ws, assignedAssociates: slots };
    });

    // A non-workstation entry is a fixed planned position too: replace it with
    // an empty placeholder so the pool keeps its planned count and the freed
    // slot can be re-filled — consistent with workstation slots and
    // fillEmptyNonWorkstationSlot.
    let newNonWorkstation = path.nonWorkstationAssociates;
    if (path.nonWorkstationAssociates) {
      const idx = path.nonWorkstationAssociates.findIndex(a => a.associateId === associateId);
      if (idx !== -1) {
        pathChanged = true;
        newNonWorkstation = path.nonWorkstationAssociates.map((entry, i) =>
          i === idx ? makeEmptySlot() : entry
        );
      }
    }

    if (pathChanged) {
      return {
        ...path,
        workstations: newWorkstations,
        nonWorkstationAssociates: newNonWorkstation,
      };
    }
    return path;
  });

  // Return the associate to the correct pool (clearing their station id and the
  // slot properties — those describe the physical slot they just vacated, not
  // the person, so they must not follow them into the pool).
  // Model A: a PA/Lead/HR associate (marked when assigned out of that pool)
  // returns to the PA/Lead/HR pool; everyone else returns to the regular
  // unassigned pool.
  const pooled: AssignmentAssociate = {
    ...assigned,
    assignedWorkstationId: undefined,
    ...NO_SLOT_PROPERTIES,
  };

  if (pooled.isPALeadHR) {
    /* istanbul ignore next -- defensive: an associate only carries isPALeadHR after
       being assigned out of a defined PA/Lead/HR pool, so the pool is present here */
    const currentPool = response.unassignedPALeadHRAssociates ?? [];
    const alreadyPooled = currentPool.some(a => a.associateId === associateId);
    const newPALeadHRPool = alreadyPooled
      ? currentPool.map(a => (a.associateId === associateId ? pooled : a))
      : [...currentPool, pooled];
    return {
      ...response,
      unassignedPALeadHRAssociates: newPALeadHRPool,
      processPaths: newProcessPaths,
    };
  }

  const alreadyPooled = response.associates.some(a => a.associateId === associateId);
  const newAssociates = alreadyPooled
    ? response.associates.map(a => (a.associateId === associateId ? pooled : a))
    : [...response.associates, pooled];

  return {
    ...response,
    associates: newAssociates,
    processPaths: newProcessPaths,
  };
}

/**
 * Build a human-readable summary of where an associate is currently assigned,
 * for display in the Unassign popup. Returns:
 *   - `"<path> → <line> → <station>"` for a workstation (line) slot, where
 *     station is the slot's per-station id when present.
 *   - `"<path>"` for a non-workstation (department) associate.
 *   - `null` when the associate is not assigned anywhere.
 */
export function describeAssignedLocation(
  processPaths: ProcessPath[],
  associateId: string
): string | null {
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      const slot = ws.assignedAssociates.find(a => a.associateId === associateId);
      if (slot) {
        const station = slot.assignedWorkstationId;
        return station
          ? `${path.processPathName} → ${ws.workstationName} → ${station}`
          : `${path.processPathName} → ${ws.workstationName}`;
      }
    }
    if (path.nonWorkstationAssociates?.some(a => a.associateId === associateId)) {
      return path.processPathName;
    }
  }
  return null;
}

/**
 * Return a copy of `response` with the given `decision` stamped onto the
 * associate identified by `associateId`, wherever they currently appear (the
 * unassigned pool, workstation slots, or non-workstation department lists).
 *
 * Used to record a planner's assign/unassign action as a `USER_OVERRIDE`
 * {@link AssignmentDecision} so the captured reason travels to the facade on
 * publish. Pure: the input is not mutated. Associates that don't match are
 * returned unchanged (same reference).
 */
export function applyDecisionToAssociate(
  response: StationAssignmentHalfData,
  associateId: string,
  decision: AssignmentDecision
): StationAssignmentHalfData {
  const stamp = (a: AssignmentAssociate): AssignmentAssociate =>
    a.associateId === associateId ? { ...a, decision } : a;

  return {
    ...response,
    associates: response.associates.map(stamp),
    unassignedPALeadHRAssociates: response.unassignedPALeadHRAssociates
      ? response.unassignedPALeadHRAssociates.map(stamp)
      : response.unassignedPALeadHRAssociates,
    processPaths: response.processPaths.map(path => ({
      ...path,
      workstations: path.workstations.map(ws => ({
        ...ws,
        assignedAssociates: ws.assignedAssociates.map(stamp),
      })),
      nonWorkstationAssociates: path.nonWorkstationAssociates
        ? path.nonWorkstationAssociates.map(stamp)
        : path.nonWorkstationAssociates,
    })),
  };
}

/**
 * Recompute the top-level `AssignmentsMetrics` object from the current
 * state of `response.associates` and `response.processPaths`.
 *
 * Does not mutate the input.
 */
export function recomputeMetrics(response: StationAssignmentHalfData): AssignmentsMetrics {
  let totalUnlocked = 0;
  let totalClockedIn = 0;
  for (const associate of response.associates) {
    if (associate.isClocked) {
      totalClockedIn += 1;
    } else {
      totalUnlocked += 1;
    }
  }

  let assignedPrimary = 0;
  let assignedCleanDecant = 0;
  let assignedResale = 0;
  let assignedProblemSolve = 0;
  let assignedIndirect = 0;

  for (const path of response.processPaths) {
    const assignedCount =
      path.workstations.reduce((sum, ws) => sum + ws.assignedAssociates.length, 0) +
      (path.nonWorkstationAssociates?.length ?? 0);

    switch (path.processPathName) {
      case PRIMARY_PATH_NAME:
        assignedPrimary += assignedCount;
        break;
      case CLEAN_DECANT_PATH_NAME:
        assignedCleanDecant += assignedCount;
        break;
      case RESALE_PATH_NAME:
        assignedResale += assignedCount;
        break;
      default:
        break;
    }

    if (path.pathType === PathType.INDIRECT) {
      if (path.processPathName === PROBLEM_SOLVE_PATH_NAME) {
        assignedProblemSolve += assignedCount;
      } else {
        assignedIndirect += assignedCount;
      }
    }
  }

  return {
    totalUnlocked,
    totalClockedIn,
    totalActual: response.associates.length,
    assignedPrimary,
    assignedProblemSolve,
    assignedResale,
    assignedCleanDecant,
    assignedIndirect,
  };
}
